import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_BASE_URL } from '../config';
import './FileShare.css';

interface Item {
  id: number;
  type: 'text' | 'file';
  content?: string;
  filename?: string;
  path?: string;
  size?: number;
  timestamp: string;
  user: string;
  remark?: string;
}

interface FileShareProps {
  token: string;
  onLogout: () => void;
}

const FileShare: React.FC<FileShareProps> = ({ token, onLogout }) => {
  const [items, setItems] = useState<Item[]>([]);
  const [textContent, setTextContent] = useState('');
  const [remark, setRemark] = useState('');
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchItems = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/items`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setItems(data);
      }
    } catch (err) {
      console.error('Failed to fetch items');
    }
  }, [token]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleTextSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!textContent.trim()) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/items/text`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ content: textContent, remark }),
      });

      if (response.ok) {
        setTextContent('');
        setRemark('');
        fetchItems();
      }
    } catch (err) {
      console.error('Failed to submit text');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('remark', remark);

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/items/file`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.ok) {
        setRemark('');
        fetchItems();
      }
    } catch (err) {
      console.error('Failed to upload file');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.indexOf('image') !== -1) {
        const blob = item.getAsFile();
        if (blob) {
          handleFileUpload(blob);
        }
      }
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const downloadFile = async (filename: string, originalName: string) => {
    try {
      const url = `${API_BASE_URL}/api/download/${filename}?name=${encodeURIComponent(originalName)}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        
        // The 'download' attribute is a fallback. The Content-Disposition header from the server should be the primary source.
        link.download = originalName; 
        
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      } else {
        console.error('Download request failed with status:', response.status);
      }
    } catch (err) {
      console.error('Failed to download file:', err);
    }
  };

  const deleteItem = async (id: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/items/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        fetchItems();
      }
    } catch (err) {
      console.error('Failed to delete item');
    }
  };

  const formatFileSize = (bytes: number) => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="file-share">
      <header className="header">
        <h1>File Share</h1>
        <button onClick={onLogout} className="logout-btn">Logout</button>
      </header>

      <div className="upload-section" onPaste={handlePaste}>
        <div className="upload-card">
          <div className="remark-input">
            <input
              type="text"
              placeholder="Add a remark (optional)"
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
            />
          </div>
          <div className="upload-actions">
            <form onSubmit={handleTextSubmit} className="text-form">
              <textarea
                value={textContent}
                onChange={(e) => setTextContent(e.target.value)}
                placeholder="Paste or type text here..."
                rows={4}
              />
              <button type="submit" disabled={loading || !textContent.trim()}>
                Share Text
              </button>
            </form>

            <div className="file-upload">
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileSelect}
                style={{ position: 'absolute', top: '-9999px', left: '-9999px' }}
              />
              <button 
                onClick={() => fileInputRef.current?.click()}
                disabled={loading}
                className="file-btn"
              >
                Upload File
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="items-list">
        {items.map((item) => (
          <div key={item.id} className="item-card">
            <div className="item-header">
              <div className="item-meta">
                <span className="item-type">{item.type}</span>
                <span className="item-date">{formatDate(item.timestamp)}</span>
                {item.remark && (
                  <span className="item-remark" title={item.remark}>{item.remark}</span>
                )}
              </div>
              <button 
                onClick={() => deleteItem(item.id)}
                className="delete-btn"
                title="Delete"
              >
                Delete
              </button>
            </div>


            {item.type === 'text' ? (
              <div className="text-content">
                <pre>{item.content}</pre>
                <button 
                  onClick={() => copyToClipboard(item.content!)}
                  className="copy-btn"
                >
                  Copy
                </button>
              </div>
            ) : (
              <div className="file-content">
                <div className="file-info">
                  <span className="filename">{item.filename}</span>
                  <span className="filesize">{formatFileSize(item.size!)}</span>
                </div>
                <button 
                  onClick={() => downloadFile(item.path!, item.filename!)}
                  className="download-btn"
                >
                  Download
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default FileShare;